import { routes } from '@/config/routes'
import { BENCHMARK_SELECTED_BENCHMARKS_COOKIE_KEY, BENCHMARK_SELECTED_VAULTS_COOKIE_KEY } from '@/constants/cookie'
import { ANALYTICS_PAGE_KEYS } from '@/constants/state'
import { BENCHMARK_ITEMS, useBenchmarkApyChart } from '@/hooks/analytics/useBenchmarkApyChart'
import { useSearchState } from '@/hooks/useSearchState'
import type { HistoricalBenchmark } from '@/queries/historicalBenchmarks.query'
import { useVaultsQuery } from '@/queries/vaults.query'
import type { VaultApyRange } from '@/types/apy'
import type { ChartPeriod } from '@/types/periods'
import type { Vault } from '@/types/vault'
import { getCookieValue } from '@/utils/cookie/cookie'
import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import { vaultIdSchema } from '@vaultsfyi/common/schemas'
import type { Network } from '@vaultsfyi/common/types'
import { formatAmount, formatDate } from '@vaultsfyi/common/utils'
import { useEffect, useRef } from 'react'
import { Link, generatePath } from 'react-router'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { ChartContainer } from '../charts/chartContainer'
import { ChartTooltip } from '../charts/chartTooltip'
import { ChartWatermark } from '../charts/chartWatermark'
import { Button } from '../common/button'
import { Loader } from '../common/loader'
import { type BenchmarkItem, VaultSelector } from './vaultSelector'

const DEFAULT_VAULT_KEYS = [
  { network: 'mainnet', vaultId: '0x23878914EFE38d27C4D67Ab83ed1b93A74D4086a' }, // AAVE v3 USDT Ethereum
  { network: 'mainnet', vaultId: '0x9D39A5DE30e57443BfF2A8307A4256c8797A3497' }, // Ethena Staked USDe Ethereum
  { network: 'mainnet', vaultId: '0x80ac24aA929eaF5013f6436cdA2a7ba190f5Cc0b' }, // Syrup USDC Ethereum
  { network: 'mainnet', vaultId: '0xa3931d71877C0E7a3148CB7Eb4463524FEc27fbD' }, // Savings USDS Ethereum
] as const

type SavedVaultKey = { network: Network; vaultId: string }

export const CustomTooltip = ({
  active,
  payload,
  label,
  keyToName,
}: {
  active?: boolean
  payload?: Array<{ dataKey: string; value: number; color: string }>
  label?: number
  keyToName: Record<string, string>
}) => {
  if (!active || !payload || !Array.isArray(payload) || payload.length === 0) return null

  const sortedPayload = [...payload].sort((a, b) => {
    const valueA = typeof a.value === 'number' ? a.value : 0
    const valueB = typeof b.value === 'number' ? b.value : 0
    return valueB - valueA
  })

  return (
    <ChartTooltip>
      <p className="mb-2 text-sm">{formatDate({ timestamp: Number(label) * SECOND_IN_MILLISECONDS })}</p>
      {sortedPayload.map((entry) => (
        <div key={entry.dataKey} className="flex items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="size-3 rounded-full" style={{ backgroundColor: entry.color }} />
            <span>{keyToName[String(entry.dataKey)] || entry.dataKey}</span>
          </div>
          <span className="font-semibold tabular-nums">
            {typeof entry.value === 'number' ? `${(entry.value * 100).toFixed(2)}%` : '—'}
          </span>
        </div>
      ))}
    </ChartTooltip>
  )
}

interface Props {
  apyRange: VaultApyRange
  chartPeriod: ChartPeriod
  includeRewards: boolean
  ethBenchmarkData?: HistoricalBenchmark
  usdBenchmarkData?: HistoricalBenchmark
  selectedVaults: Vault[]
  onVaultsChange: (vaults: Vault[]) => void
  selectedBenchmarks: BenchmarkItem[]
  onBenchmarksChange: (benchmarks: BenchmarkItem[]) => void
}

export const BenchmarkApyComparison = ({
  apyRange,
  chartPeriod,
  includeRewards,
  ethBenchmarkData,
  usdBenchmarkData,
  selectedVaults,
  onVaultsChange,
  selectedBenchmarks,
  onBenchmarksChange,
}: Props) => {
  const hasInitialized = useRef(false)
  const { data: vaultsAll } = useVaultsQuery({ initialData: undefined })
  const { searchState } = useSearchState({})
  const searchStateRef = useRef(searchState)
  searchStateRef.current = searchState

  useEffect(() => {
    if (!vaultsAll || hasInitialized.current) return
    hasInitialized.current = true

    const urlVaultKeys = searchStateRef.current.getAll(ANALYTICS_PAGE_KEYS.benchmarks.selectedVaults)
    const urlBenchmarkKeys = searchStateRef.current.getAll(ANALYTICS_PAGE_KEYS.benchmarks.selectedBenchmarks)
    const hasUrlState = urlVaultKeys.length > 0 || urlBenchmarkKeys.length > 0

    if (hasUrlState) {
      const restoredVaults = urlVaultKeys
        .map((key) => {
          const colonIdx = key.indexOf(':')
          if (colonIdx === -1) return undefined
          const network = key.slice(0, colonIdx)
          const parsedVaultId = vaultIdSchema.safeParse(key.slice(colonIdx + 1))
          if (!parsedVaultId.success) return undefined
          return vaultsAll.find((v) => v.network.name === network && v.vaultId === parsedVaultId.data)
        })
        .filter(Boolean) as Vault[]
      onVaultsChange(restoredVaults)

      if (urlBenchmarkKeys.length > 0) {
        const restoredBenchmarks = BENCHMARK_ITEMS.filter((b) => urlBenchmarkKeys.includes(b.id))
        if (restoredBenchmarks.length > 0) onBenchmarksChange(restoredBenchmarks)
      }
      return
    }

    let restoredVaults: Vault[] = []

    const vaultCookieValue = getCookieValue(BENCHMARK_SELECTED_VAULTS_COOKIE_KEY)
    if (vaultCookieValue) {
      try {
        const savedKeys = JSON.parse(vaultCookieValue) as SavedVaultKey[]
        restoredVaults = savedKeys
          .map((key) => vaultsAll.find((v) => v.network.name === key.network && v.vaultId === key.vaultId))
          .filter(Boolean) as Vault[]
      } catch (error) {
        console.error('Error parsing selected vaults from cookie:', error)
      }
    }

    if (restoredVaults.length === 0) {
      restoredVaults = DEFAULT_VAULT_KEYS.map((key) =>
        vaultsAll.find((v) => v.network.name === key.network && v.vaultId === key.vaultId)
      ).filter(Boolean) as Vault[]
    }

    let restoredBenchmarks: BenchmarkItem[] = []

    const benchmarkCookieValue = getCookieValue(BENCHMARK_SELECTED_BENCHMARKS_COOKIE_KEY)
    if (benchmarkCookieValue) {
      try {
        const savedKeys = JSON.parse(benchmarkCookieValue) as string[]
        restoredBenchmarks = BENCHMARK_ITEMS.filter((b) => savedKeys.includes(b.id))
      } catch (error) {
        console.error('Error parsing selected benchmarks from cookie:', error)
      }
    }

    onVaultsChange(restoredVaults)
    onBenchmarksChange(restoredBenchmarks)
  }, [vaultsAll, onVaultsChange, onBenchmarksChange])

  const {
    vaults,
    positionsList,
    isLoading,
    benchmarkApyMap,
    chartData,
    dataKeys,
    sortedDataKeys,
    nonBenchmarkKeys,
    keyToName,
    getKeyColor,
    getStrokeWidth,
    handleSetSelectedVaults,
    handleSetSelectedBenchmarks,
  } = useBenchmarkApyChart({
    apyRange,
    chartPeriod,
    includeRewards,
    ethBenchmarkData,
    usdBenchmarkData,
    selectedVaults,
    selectedBenchmarks,
    onVaultsChange,
    onBenchmarksChange,
  })

  return (
    <div className="flex flex-col items-end gap-2">
      <VaultSelector
        vaults={vaults ?? []}
        selectedVaults={selectedVaults}
        setSelectedVaults={handleSetSelectedVaults}
        benchmarks={BENCHMARK_ITEMS}
        selectedBenchmarks={selectedBenchmarks}
        setSelectedBenchmarks={handleSetSelectedBenchmarks}
        positionsList={positionsList}
        isLoading={!vaults}
        apyRange={apyRange}
        includeRewards={includeRewards}
        benchmarkApyMap={benchmarkApyMap}
      />
      <ChartContainer className="h-60 w-full">
        {isLoading ? (
          <Loader />
        ) : (
          <ResponsiveContainer>
            <AreaChart data={chartData}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgb(var(--border))" />
              <XAxis
                dataKey="timestamp"
                tickLine={false}
                tickMargin={12}
                type="number"
                interval="preserveStartEnd"
                domain={['dataMin', 'dataMax']}
                tickCount={7}
                tickFormatter={(timestamp) => formatDate({ timestamp: timestamp * SECOND_IN_MILLISECONDS })}
              />
              <YAxis
                width={52}
                tickLine={false}
                tickMargin={8}
                tickFormatter={(value) =>
                  `${formatAmount(value * 100, { notation: 'compact', maximumFractionDigits: 1 })}%`
                }
              />
              <Tooltip content={<CustomTooltip keyToName={keyToName} />} />
              {dataKeys.map((key) => {
                const vaultIndex = nonBenchmarkKeys.indexOf(key)
                return (
                  <Area
                    key={key}
                    dataKey={key}
                    type="monotone"
                    stroke={getKeyColor(key, vaultIndex)}
                    strokeWidth={getStrokeWidth(key)}
                    fill="transparent"
                    name={key}
                  />
                )
              })}
              <ChartWatermark />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </ChartContainer>
      {dataKeys.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {sortedDataKeys.map((key: string) => {
            const vaultIndex = nonBenchmarkKeys.indexOf(key)
            const vault = selectedVaults.find((v) => `${v.vaultId}-${v.network.name}` === key)
            const content = (
              <>
                <div
                  className="size-3 min-w-3 rounded-full"
                  style={{ backgroundColor: getKeyColor(key, vaultIndex) }}
                />
                <span className="whitespace-nowrap text-[13px]">{keyToName[key]}</span>
              </>
            )
            return vault ? (
              <Button key={key} variant="default" size="default" className="flex items-center gap-2 p-1" asChild>
                <Link
                  to={generatePath(routes.opportunity, { vaultId: vault.vaultId, network: vault.network.name })}
                  target="_blank"
                  rel="noopener noreferrer"
                  prefetch="intent"
                >
                  {content}
                </Link>
              </Button>
            ) : (
              <div key={key} className="flex items-center gap-2 p-1 text-body-foreground">
                {content}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
